import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Application, Container, Graphics } from 'pixi.js';
import { GameHUD, GameMenu, OtterSelector } from '../components';
import { useGameProgress } from '../hooks';
import { checkRectCollision, clamp, lerp } from '../utils/collision';
import {
  createProceduralOtter,
  createProceduralFood,
  createProceduralObstacle,
  createProceduralPowerUp,
} from '../utils/sprites';
import {
  riverRushOtters,
  biomes,
  getFoodForBiome,
  getObstaclesForBiome,
  powerUps,
  getRandomFact,
} from './data';
import type { RiverRushState, PowerUp, Vector2, Bounds } from '../types';

interface GameObject {
  id: string;
  type: 'food' | 'obstacle' | 'powerup';
  container: Container;
  bounds: Bounds;
  data: {
    points?: number;
    damage?: number;
    canBreak?: boolean;
    effect?: PowerUp['effect'];
    duration?: number;
    preferredBy?: string[];
  };
}

// Otter visual size vs hitbox - hitbox is smaller for fairer collisions
const OTTER_SIZE = { width: 70, height: 35 };
const OTTER_HITBOX = { width: 40, height: 20 }; // Smaller hitbox for forgiveness
const SPAWN_MARGIN = 100;

// Collision shrink factor - makes hitboxes smaller than visual for fairness
const HITBOX_SHRINK = 0.6;

export function RiverRushGame() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const appRef = useRef<Application | null>(null);

  // Game objects refs
  const otterRef = useRef<Container | null>(null);
  const gameObjectsRef = useRef<GameObject[]>([]);
  const backgroundLayersRef = useRef<Container[]>([]);

  // Game state
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'paused' | 'gameover'>('menu');
  const [showOtterSelector, setShowOtterSelector] = useState(false);
  const [state, setState] = useState<RiverRushState>({
    score: 0,
    distance: 0,
    health: 5,
    maxHealth: 5,
    combo: 0,
    speed: 80, // Start slower, will accelerate
    activePowerUp: null,
    powerUpEndTime: 0,
    isPaused: false,
    isGameOver: false,
    selectedOtter: riverRushOtters[0],
    currentBiome: 'kelp-forest',
  });

  const [foodCollected, setFoodCollected] = useState(0);
  const [isAppReady, setIsAppReady] = useState(false);
  const [gameOverFact, setGameOverFact] = useState('');

  // Refs for game loop (to avoid stale closures)
  const moveDirectionRef = useRef<Vector2>({ x: 0, y: 0 });
  const otterPositionRef = useRef<Vector2>({ x: 80, y: 350 });
  const targetYRef = useRef<number | null>(null); // Touch target - otter moves toward this
  const lastSpawnTimeRef = useRef<number>(0);
  const objectIdCounter = useRef<number>(0);
  const gameLoopRef = useRef<number | null>(null);
  const stateRef = useRef(state);
  const gameStateRef = useRef(gameState);
  const lastTimeRef = useRef<number>(0);

  // Keep refs in sync
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  const { progress, updateRiverRushScore, unlockOtter } = useGameProgress();

  // Get available otters with unlock status
  const availableOtters = riverRushOtters.map(otter => ({
    ...otter,
    unlocked: otter.id === 'sea-otter' || progress.riverRush.unlockedOtters.includes(otter.id),
  }));

  // Initialize PixiJS
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let app: Application;
    let destroyed = false;

    const initApp = async () => {
      app = new Application();

      await app.init({
        width: container.clientWidth || 800,
        height: container.clientHeight || 600,
        backgroundColor: 0x2e86ab,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
        antialias: true,
      });

      if (destroyed) {
        app.destroy(true);
        return;
      }

      appRef.current = app;
      container.appendChild(app.canvas);
      canvasRef.current = app.canvas as HTMLCanvasElement;

      // Create background
      const bg = new Graphics();
      const biomeConfig = biomes.find(b => b.id === 'kelp-forest') || biomes[0];
      bg.rect(0, 0, app.screen.width, app.screen.height);
      bg.fill(biomeConfig.waterColor);
      app.stage.addChild(bg);
      backgroundLayersRef.current.push(bg as unknown as Container);

      // Create water waves effect
      for (let i = 0; i < 3; i++) {
        const wave = new Graphics();
        wave.rect(0, 0, app.screen.width * 2, 20);
        wave.fill({ color: 0xffffff, alpha: 0.1 });
        wave.y = 100 + i * 200;
        wave.x = -app.screen.width;
        app.stage.addChild(wave);
        backgroundLayersRef.current.push(wave as unknown as Container);
      }

      // Create otter
      const otter = createProceduralOtter(0x8B4513);
      otter.x = otterPositionRef.current.x;
      otter.y = otterPositionRef.current.y;
      otter.pivot.set(OTTER_SIZE.width / 2, OTTER_SIZE.height / 2);
      app.stage.addChild(otter);
      otterRef.current = otter;

      setIsAppReady(true);
    };

    initApp();

    return () => {
      destroyed = true;
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
    };
  }, []);

  // Handle window resize and orientation changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isAppReady) return;

    const handleResize = () => {
      const app = appRef.current;
      if (!app) return;

      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;

      if (newWidth > 0 && newHeight > 0) {
        // Resize the renderer
        app.renderer.resize(newWidth, newHeight);

        // Update background to fill new size
        const bg = backgroundLayersRef.current[0];
        if (bg && 'clear' in bg) {
          const graphics = bg as unknown as Graphics;
          graphics.clear();
          const biomeConfig = biomes.find(b => b.id === stateRef.current.currentBiome) || biomes[0];
          graphics.rect(0, 0, newWidth, newHeight);
          graphics.fill(biomeConfig.waterColor);
        }

        // Update wave layers width
        backgroundLayersRef.current.forEach((layer, i) => {
          if (i > 0 && 'clear' in layer) {
            const wave = layer as unknown as Graphics;
            wave.clear();
            wave.rect(0, 0, newWidth * 2, 20);
            wave.fill({ color: 0xffffff, alpha: 0.1 });
          }
        });

        // Clamp otter position to new bounds
        if (otterRef.current) {
          otterPositionRef.current.y = clamp(
            otterPositionRef.current.y,
            60,
            newHeight - 60
          );
          otterRef.current.y = otterPositionRef.current.y;
        }
      }
    };

    // Listen to both resize and orientationchange for mobile
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      // Small delay to let browser finish orientation change
      setTimeout(handleResize, 100);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isAppReady]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
        moveDirectionRef.current = { x: 0, y: -1 };
        e.preventDefault();
      } else if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') {
        moveDirectionRef.current = { x: 0, y: 1 };
        e.preventDefault();
      } else if (e.key === 'Escape') {
        if (gameStateRef.current === 'playing') {
          setGameState('paused');
        } else if (gameStateRef.current === 'paused') {
          setGameState('playing');
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'w', 'W', 's', 'S'].includes(e.key)) {
        moveDirectionRef.current = { x: 0, y: 0 };
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Mouse/Touch controls - otter moves directly toward finger/mouse position
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isDragging = false;

    const getYFromEvent = (e: MouseEvent | Touch): number => {
      const rect = container.getBoundingClientRect();
      return e.clientY - rect.top;
    };

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      targetYRef.current = getYFromEvent(e);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        targetYRef.current = getYFromEvent(e);
      }
    };

    const handleMouseUp = () => {
      isDragging = false;
      targetYRef.current = null;
      moveDirectionRef.current = { x: 0, y: 0 };
    };

    const handleTouchStart = (e: TouchEvent) => {
      // Only handle game touch controls when playing - allow button clicks otherwise
      if (gameStateRef.current !== 'playing') return;

      if (e.touches.length > 0) {
        const touch = e.touches[0];
        const rect = container.getBoundingClientRect();
        const touchY = touch.clientY - rect.top;

        // Don't intercept touches in the HUD area (top 70px) or hint area (bottom 60px)
        // This allows pause button and other UI elements to receive touch events
        if (touchY < 70 || touchY > rect.height - 60) {
          return;
        }

        isDragging = true;
        targetYRef.current = getYFromEvent(touch);
        e.preventDefault();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Only handle game touch controls when playing
      if (gameStateRef.current !== 'playing') return;

      if (isDragging && e.touches.length > 0) {
        targetYRef.current = getYFromEvent(e.touches[0]);
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Only handle game touch controls when playing
      if (gameStateRef.current !== 'playing') return;

      isDragging = false;
      targetYRef.current = null;
      moveDirectionRef.current = { x: 0, y: 0 };
      e.preventDefault();
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  // Spawn game objects
  const spawnObject = useCallback((type: 'food' | 'obstacle' | 'powerup') => {
    const app = appRef.current;
    if (!app) {
      return;
    }

    const currentState = stateRef.current;
    let container: Container;
    let data: GameObject['data'] = {};
    let width = 30;
    let height = 30;

    if (type === 'food') {
      const foods = getFoodForBiome(currentState.currentBiome);
      const food = foods[Math.floor(Math.random() * foods.length)];
      container = createProceduralFood(food.sprite);
      data = { points: food.points, preferredBy: food.preferredBy };
      width = 25;
      height = 20;
    } else if (type === 'obstacle') {
      const obs = getObstaclesForBiome(currentState.currentBiome);
      const obstacle = obs[Math.floor(Math.random() * obs.length)];
      container = createProceduralObstacle(obstacle.sprite);
      data = { damage: obstacle.damage, canBreak: obstacle.canBreak };
      width = obstacle.sprite === 'shark' ? 70 : obstacle.sprite === 'log' ? 60 : 40;
      height = obstacle.sprite === 'shark' ? 30 : obstacle.sprite === 'log' ? 20 : 35;
    } else {
      const powerUp = powerUps[Math.floor(Math.random() * powerUps.length)];
      container = createProceduralPowerUp(powerUp.effect);
      data = { effect: powerUp.effect, duration: powerUp.duration };
      width = 35;
      height = 35;
    }

    // Position at right edge, random Y
    const x = app.screen.width + SPAWN_MARGIN;
    const y = Math.random() * (app.screen.height - 150) + 75;

    container.x = x;
    container.y = y;
    app.stage.addChild(container);

    const gameObject: GameObject = {
      id: `obj-${objectIdCounter.current++}`,
      type,
      container,
      bounds: { x, y, width, height },
      data,
    };

    gameObjectsRef.current.push(gameObject);
  }, []);

  // Main game loop
  useEffect(() => {
    if (!isAppReady) return;

    const gameLoop = (timestamp: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const deltaTime = Math.min((timestamp - lastTimeRef.current) / 1000, 0.1); // Cap delta to avoid large jumps
      lastTimeRef.current = timestamp;

      // Only run game logic when playing
      if (gameStateRef.current !== 'playing') {
        gameLoopRef.current = requestAnimationFrame(gameLoop);
        return;
      }


      const app = appRef.current;
      const otter = otterRef.current;
      if (!app || !otter) {
        gameLoopRef.current = requestAnimationFrame(gameLoop);
        return;
      }

      const currentState = stateRef.current;

      // Update otter position based on input
      const moveSpeed = 350 * deltaTime;
      const direction = moveDirectionRef.current;
      const touchTarget = targetYRef.current;

      // Touch/mouse control: move directly toward finger position
      if (touchTarget !== null) {
        const diff = touchTarget - otterPositionRef.current.y;
        const deadzone = 10; // Don't move if very close to target

        if (Math.abs(diff) > deadzone) {
          // Move toward target at a speed proportional to distance (faster when far)
          const speed = Math.min(Math.abs(diff) * 8, 600) * deltaTime;
          const moveAmount = Math.sign(diff) * speed;
          otterPositionRef.current.y = clamp(
            otterPositionRef.current.y + moveAmount,
            60,
            app.screen.height - 60
          );
        }
      }
      // Keyboard control: use direction
      else if (direction.y !== 0) {
        const newY = otterPositionRef.current.y + direction.y * moveSpeed;
        otterPositionRef.current.y = clamp(newY, 60, app.screen.height - 60);
      }

      // Smooth visual position (more responsive now)
      otter.y = lerp(otter.y, otterPositionRef.current.y, 0.25);

      // Slight bobbing animation
      otter.rotation = Math.sin(timestamp / 200) * 0.05;

      // Update distance and speed - unlimited acceleration
      const distanceGain = currentState.speed * deltaTime;
      // Accelerate: gain ~2 speed per second, no limit!
      const speedIncrease = deltaTime * 2.0;

      setState(prev => ({
        ...prev,
        distance: prev.distance + distanceGain / 100,
        speed: prev.speed + speedIncrease, // No max limit
      }));

      // Move background waves (only when playing, not game over)
      if (!currentState.isGameOver) {
        backgroundLayersRef.current.forEach((layer, i) => {
          if (i > 0) {
            layer.x -= (currentState.speed * 0.3 + i * 10) * deltaTime;
            if (layer.x < -app.screen.width) {
              layer.x = 0;
            }
          }
        });
      }

      // Spawn objects - spawn interval decreases as speed increases
      const now = Date.now();
      // Base interval 500ms, decreases by 10% of speed increase from base (80)
      // At speed 80: 500ms, at speed 180: 490ms, at speed 1080: 400ms, etc.
      const speedBonus = Math.max(0, currentState.speed - 80);
      const spawnInterval = Math.max(150, 500 - speedBonus * 0.1); // Min 150ms interval

      if (now - lastSpawnTimeRef.current > spawnInterval) {
        // Calculate difficulty scaling based on distance traveled
        // At 0m: obstacleRatio = 0.15, at 500m+: obstacleRatio = 0.70
        const difficultyProgress = Math.min(currentState.distance / 500, 1); // 0 to 1 over 500m
        const obstacleRatio = 0.15 + (0.55 * difficultyProgress); // 15% to 70%

        // Spawn count also increases with speed (1 at base, more as speed increases)
        // Every 200 speed over base = +1 spawn chance
        const extraSpawns = Math.floor(speedBonus / 200);
        const spawnCount = 1 + extraSpawns;

        for (let i = 0; i < spawnCount; i++) {
          // Always spawn something (90% chance per spawn)
          if (Math.random() < 0.9) {
            const roll = Math.random();
            if (roll < obstacleRatio) {
              spawnObject('obstacle');
            } else {
              spawnObject('food');
            }
          }
        }

        // Power-ups spawn independently (5% chance)
        if (Math.random() < 0.05) {
          spawnObject('powerup');
        }
        lastSpawnTimeRef.current = now;
      }

      // Update game objects - use smaller hitbox for fairer collisions
      const otterBounds: Bounds = {
        x: otter.x - OTTER_HITBOX.width / 2,
        y: otter.y - OTTER_HITBOX.height / 2,
        width: OTTER_HITBOX.width,
        height: OTTER_HITBOX.height,
      };

      const objectsToRemove: string[] = [];

      gameObjectsRef.current.forEach(obj => {
        // Move object left
        const moveAmount = currentState.speed * deltaTime;
        obj.container.x -= moveAmount;
        obj.bounds.x -= moveAmount;

        // Remove if off screen
        if (obj.container.x < -SPAWN_MARGIN) {
          objectsToRemove.push(obj.id);
          return;
        }

        // Check collision with otter - shrink obstacle hitbox for fairness
        const shrunkBounds: Bounds = {
          x: obj.bounds.x + obj.bounds.width * (1 - HITBOX_SHRINK) / 2,
          y: obj.bounds.y + obj.bounds.height * (1 - HITBOX_SHRINK) / 2,
          width: obj.bounds.width * HITBOX_SHRINK,
          height: obj.bounds.height * HITBOX_SHRINK,
        };
        if (checkRectCollision(otterBounds, shrunkBounds)) {
          if (obj.type === 'food') {
            // Collect food
            let points = obj.data.points || 10;

            // Bonus for preferred food
            if (obj.data.preferredBy?.includes(currentState.selectedOtter.speciesId)) {
              points = Math.floor(points * 1.5);
            }

            // Power-up bonus
            if (currentState.activePowerUp?.effect === 'appetite') {
              points *= 3;
            }

            setState(prev => ({
              ...prev,
              score: prev.score + points * (1 + Math.floor(prev.combo) * 0.1),
              combo: prev.combo + 1,
            }));
            setFoodCollected(prev => prev + 1);
            objectsToRemove.push(obj.id);
          } else if (obj.type === 'obstacle') {
            // Check if can break or invincible
            const canBreak = obj.data.canBreak && currentState.activePowerUp?.effect === 'tool';
            const isInvincible = currentState.activePowerUp?.effect === 'speed';

            if (canBreak || isInvincible) {
              // Break obstacle, add small bonus
              setState(prev => ({ ...prev, score: prev.score + 5 }));
            } else {
              // Take damage - cap at 1 for fairness (no instant kills)
              const damage = Math.min(obj.data.damage || 1, 1);
              setState(prev => {
                const newHealth = prev.health - damage;
                if (newHealth <= 0) {
                  setGameOverFact(getRandomFact());
                  setGameState('gameover');
                  return { ...prev, health: 0, isGameOver: true, combo: 0 };
                }
                return { ...prev, health: newHealth, combo: 0 };
              });
            }
            objectsToRemove.push(obj.id);
          } else if (obj.type === 'powerup') {
            // Activate power-up
            const powerUp = powerUps.find(p => p.effect === obj.data.effect);
            if (powerUp) {
              setState(prev => ({
                ...prev,
                activePowerUp: powerUp,
                powerUpEndTime: Date.now() + powerUp.duration * 1000,
              }));
            }
            objectsToRemove.push(obj.id);
          }
        }
      });

      // Remove collected/off-screen objects
      objectsToRemove.forEach(id => {
        const index = gameObjectsRef.current.findIndex(obj => obj.id === id);
        if (index !== -1) {
          const obj = gameObjectsRef.current[index];
          app.stage.removeChild(obj.container);
          obj.container.destroy();
          gameObjectsRef.current.splice(index, 1);
        }
      });

      // Check power-up expiration
      if (currentState.activePowerUp && Date.now() > currentState.powerUpEndTime) {
        setState(prev => ({ ...prev, activePowerUp: null, powerUpEndTime: 0 }));
      }

      // Decay combo over time
      setState(prev => ({
        ...prev,
        combo: Math.max(0, prev.combo - deltaTime * 0.3),
      }));

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [isAppReady, spawnObject]);

  // Handle game start
  const handleStart = useCallback(() => {
    if (!isAppReady) {
      console.log('App not ready yet');
      return;
    }

    // Reset otter position
    if (otterRef.current) {
      otterRef.current.y = 350;
    }
    otterPositionRef.current = { x: 80, y: 350 };

    // Clear existing objects
    gameObjectsRef.current.forEach(obj => {
      appRef.current?.stage.removeChild(obj.container);
      obj.container.destroy();
    });
    gameObjectsRef.current = [];

    // Reset state - start with 5 health
    setState({
      score: 0,
      distance: 0,
      health: 5 + progress.riverRush.upgrades.thickerFur,
      maxHealth: 5 + progress.riverRush.upgrades.thickerFur,
      combo: 0,
      speed: 80 + progress.riverRush.upgrades.quickReflexes * 10, // Start slow, upgrades help
      activePowerUp: null,
      powerUpEndTime: 0,
      isPaused: false,
      isGameOver: false,
      selectedOtter: state.selectedOtter,
      currentBiome: 'kelp-forest',
    });
    setFoodCollected(0);
    lastSpawnTimeRef.current = Date.now();
    lastTimeRef.current = 0;

    setGameState('playing');

    // Force spawn some initial objects after a short delay
    setTimeout(() => {
      spawnObject('food');
      spawnObject('food');
      spawnObject('obstacle');
    }, 100);
  }, [isAppReady, state.selectedOtter, progress.riverRush.upgrades, spawnObject]);

  // Handle game over
  const handleGameOver = useCallback(() => {
    updateRiverRushScore(state.score, state.distance, foodCollected);

    // Check for unlocks
    if (progress.riverRush.totalDistance + state.distance >= 5000 && !progress.riverRush.unlockedOtters.includes('giant-otter')) {
      unlockOtter('giant-otter');
    }
    if (progress.riverRush.totalFoodCollected + foodCollected >= 500 && !progress.riverRush.unlockedOtters.includes('eurasian-otter')) {
      unlockOtter('eurasian-otter');
    }
    if (state.score >= 10000 && !progress.riverRush.unlockedOtters.includes('asian-small-clawed')) {
      unlockOtter('asian-small-clawed');
    }
    if (progress.riverRush.gamesPlayed >= 9 && !progress.riverRush.unlockedOtters.includes('smooth-coated')) {
      unlockOtter('smooth-coated');
    }
    if (progress.riverRush.gamesPlayed >= 19 && !progress.riverRush.unlockedOtters.includes('river-otter')) {
      unlockOtter('river-otter');
    }
  }, [state.score, state.distance, foodCollected, updateRiverRushScore, unlockOtter, progress.riverRush]);

  // Effect to handle game over
  useEffect(() => {
    if (state.isGameOver && gameState === 'gameover') {
      handleGameOver();
    }
  }, [state.isGameOver, gameState, handleGameOver]);

  // Handle otter selection
  const handleSelectOtter = useCallback((otterId: string) => {
    const otter = availableOtters.find(o => o.id === otterId);
    if (otter) {
      setState(prev => ({ ...prev, selectedOtter: otter }));
    }
  }, [availableOtters]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-screen bg-otter-900 overflow-hidden select-none"
      style={{ touchAction: 'none' }}
      tabIndex={0}
    >
      {/* PixiJS canvas will be appended here */}

      {/* HUD */}
      {gameState === 'playing' && (
        <GameHUD
          score={Math.floor(state.score)}
          health={state.health}
          maxHealth={state.maxHealth}
          distance={state.distance}
          combo={Math.floor(state.combo)}
          powerUp={state.activePowerUp?.name}
          powerUpTimeLeft={(state.powerUpEndTime - Date.now()) / 1000}
          onPause={() => setGameState('paused')}
        />
      )}

      {/* Menus */}
      {gameState === 'menu' && (
        <GameMenu
          type="start"
          title="Otter River Rush"
          subtitle={`Playing as ${state.selectedOtter.name}`}
          onStart={handleStart}
          onHome={() => navigate('/games')}
          onSelectOtter={() => setShowOtterSelector(true)}
        />
      )}

      {gameState === 'paused' && (
        <GameMenu
          type="pause"
          onResume={() => setGameState('playing')}
          onRestart={handleStart}
          onHome={() => navigate('/games')}
        />
      )}

      {gameState === 'gameover' && (
        <GameMenu
          type="gameover"
          score={Math.floor(state.score)}
          highScore={progress.riverRush.highScore}
          distance={state.distance}
          foodCollected={foodCollected}
          fact={gameOverFact}
          onRestart={handleStart}
          onHome={() => navigate('/games')}
          onSelectOtter={() => setShowOtterSelector(true)}
        />
      )}

      {/* Otter Selector */}
      <OtterSelector
        isOpen={showOtterSelector}
        otters={availableOtters}
        selectedOtterId={state.selectedOtter.id}
        onSelect={handleSelectOtter}
        onClose={() => setShowOtterSelector(false)}
      />

      {/* Touch hint for mobile */}
      {gameState === 'playing' && (
        <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
          <p className="text-white/40 text-xs">
            Touch & drag to move the otter | Keyboard: W/S or Arrow keys
          </p>
        </div>
      )}

    </div>
  );
}
